import { datadogLogs } from '@datadog/browser-logs'
import {
    DEFAULT_TRACKED_RESOURCE_HEADERS,
    DefaultPrivacyLevel,
    datadogRum,
} from '@datadog/browser-rum'

let isDatadogInitialized = false
let isRouteHookRegistered = false
let isVueErrorHandlerRegistered = false

function getConfigValue(value) {
    if (!value || value === 'undefined' || value === 'null') {
        return ''
    }

    return value
}

function isDatadogEnabled() {
    const enabled = getConfigValue(process.env.DATADOG_ENABLED)

    if (enabled === 'false' || enabled === '0') {
        return false
    }

    return Boolean(
        getConfigValue(process.env.DATADOG_APPLICATION_ID) &&
            getConfigValue(process.env.DATADOG_CLIENT_TOKEN)
    )
}

function getPrivacyLevel() {
    const privacyLevel = getConfigValue(process.env.DATADOG_DEFAULT_PRIVACY_LEVEL)

    if (privacyLevel === DefaultPrivacyLevel.MASK) {
        return DefaultPrivacyLevel.MASK
    }

    if (privacyLevel === DefaultPrivacyLevel.MASK_USER_INPUT) {
        return DefaultPrivacyLevel.MASK_USER_INPUT
    }

    return DefaultPrivacyLevel.ALLOW
}

function getGlobalContext(apiBaseUrl) {
    return {
        api_base_url: apiBaseUrl,
        front_branch: getConfigValue(process.env.BRANCHNAME),
    }
}

function getAllowedTracingUrls(apiBaseUrl) {
    if (!apiBaseUrl) {
        return []
    }

    return [
        {
            match: apiBaseUrl,
            propagatorTypes: ['datadog'],
        },
    ]
}

function getRouteContext(route) {
    if (!route) {
        return {}
    }

    return {
        route_name: route.name,
        route_path: route.path,
        route_full_path: route.fullPath,
    }
}

function getVueComponentName(vm) {
    if (!vm || !vm.$options) {
        return 'unknown'
    }

    return (
        vm.$options.name ||
        vm.$options._componentTag ||
        vm.$options.__file ||
        'anonymous'
    )
}

function getVueComponentStack(vm) {
    const componentStack = []
    let currentVm = vm

    while (currentVm && componentStack.length < 10) {
        componentStack.push(getVueComponentName(currentVm))
        currentVm = currentVm.$parent
    }

    return componentStack
}

function getVueErrorContext(vm, info) {
    return {
        vue_info: info,
        component_name: getVueComponentName(vm),
        component_file: vm?.$options?.__file,
        component_stack: getVueComponentStack(vm),
    }
}

function updateDatadogView(route) {
    if (!isDatadogInitialized || !route) {
        return
    }

    datadogRum.setViewName(route.name || route.path || 'unknown')
    datadogRum.setViewContext(getRouteContext(route))
}

function registerRouterHook(router) {
    if (!router || isRouteHookRegistered) {
        return
    }

    isRouteHookRegistered = true
    router.afterEach((to) => {
        updateDatadogView(to)
    })

    updateDatadogView(router.currentRoute)
}

export function registerDatadogVueErrorHandler(Vue) {
    if (!Vue || isVueErrorHandlerRegistered) {
        return false
    }

    const previousErrorHandler = Vue.config.errorHandler

    Vue.config.errorHandler = (error, vm, info) => {
        if (isDatadogInitialized) {
            datadogRum.addError(error, getVueErrorContext(vm, info))
        }

        if (typeof previousErrorHandler === 'function') {
            previousErrorHandler(error, vm, info)
            return
        }

        console.error(error)
    }

    isVueErrorHandlerRegistered = true
    return true
}

export function initDatadog({ router, apiBaseUrl }) {
    if (isDatadogInitialized || !isDatadogEnabled()) {
        return false
    }

    try {
        const normalizedApiBaseUrl = getConfigValue(apiBaseUrl)
        const commonConfig = {
            clientToken: getConfigValue(process.env.DATADOG_CLIENT_TOKEN),
            site: getConfigValue(process.env.DATADOG_SITE) || 'datadoghq.com',
            service: getConfigValue(process.env.DATADOG_SERVICE) || 'rd_front',
            env: getConfigValue(process.env.DATADOG_ENV),
            version: getConfigValue(process.env.DATADOG_VERSION),
            sessionSampleRate: 100,
            silentMultipleInit: true,
        }

        datadogRum.init({
            ...commonConfig,
            applicationId: getConfigValue(process.env.DATADOG_APPLICATION_ID),
            allowedTracingUrls: getAllowedTracingUrls(normalizedApiBaseUrl),
            defaultPrivacyLevel: getPrivacyLevel(),
            enablePrivacyForActionName: true,
            // sessionReplaySampleRate: 100,
            // trackLongTasks: true,
            // trackResourceHeaders: [
            //     ...DEFAULT_TRACKED_RESOURCE_HEADERS.map((name) => ({ name })),
            // ],
            // trackResources: true,
            // trackUserInteractions: true,
            // traceSampleRate: 100,

            // RUM Measure
            sessionSampleRate: 100,

            // Session Replay
            sessionReplaySampleRate: 100,

            // Browser Profiling
            profilingSampleRate: 25,

            // Performance
            trackLongTasks: true,
            trackResources: true,
            trackUserInteractions: true,

            // APM
            traceSampleRate: 100,

            trackResourceHeaders: [
                ...DEFAULT_TRACKED_RESOURCE_HEADERS.map(
                    (name) => ({ name })
                ),
            ],
        })

        datadogLogs.init({
            ...commonConfig,
            forwardConsoleLogs: ['warn', 'error'],
            forwardErrorsToLogs: true,
            forwardReports: 'all',
        })

        const globalContext = getGlobalContext(normalizedApiBaseUrl)
        datadogRum.setGlobalContext(globalContext)
        datadogLogs.setGlobalContext(globalContext)

        isDatadogInitialized = true
        registerRouterHook(router)

        return true
    } catch (error) {
        console.warn('Datadog initialization failed', error)
        return false
    }
}

export function setDatadogUser(usuario) {
    if (!isDatadogInitialized || !usuario) {
        return
    }

    const userId = usuario.id_usuario || usuario.id || usuario.email || usuario.login

    if (!userId) {
        return
    }

    const user = {
        id: String(userId),
        email: usuario.email,
        name: usuario.nome || usuario.login,
        cargo: usuario.cargo,
        id_cargo: usuario.id_cargo,
        id_fornecedor: usuario.id_fornecedor,
    }

    datadogRum.setUser(user)
    datadogLogs.setUser(user)
}

export function clearDatadogUser() {
    if (!isDatadogInitialized) {
        return
    }

    datadogRum.clearUser()
    datadogLogs.clearUser()
}
